import { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Layout } from '../components/layout/Layout';
import { contactAPI } from '../lib/api';
import { toast } from 'sonner';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await contactAPI.submitMessage(formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-slate-50" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-amber-700 font-medium mb-2">Get In Touch</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              We're Here to Help
            </h1>
            <p className="text-lg text-slate-600">
              Have questions about our Credit Restoration services? Our team is ready 
              to assist you on your journey to financial freedom.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white" data-testid="contact-form-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
                Contact Information
              </h2>
              <p className="text-slate-600 mb-8">
                Reach out to us through any of the following channels. We typically 
                respond within 24-48 business hours.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email</h3>
                    <a 
                      href="mailto:support@tampsolution.com" 
                      className="text-amber-700 hover:underline"
                    >
                      support@tampsolution.com
                    </a>
                    <p className="text-sm text-slate-500 mt-1">
                      Best for detailed inquiries
                    </p>
                  </div>
                </div>

                {/* Book a Call Section */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-6 w-6 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Schedule a Consultation</h3>
                    <p className="text-sm text-slate-500 mt-1 mb-3">
                      Book a free 15-minute call to discuss your credit goals
                    </p>
                    <a 
                      href="https://calendly.com/tamperis94/30min" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-teal-600 hover:bg-teal-700 gap-2" data-testid="contact-book-call-btn">
                        <Calendar className="h-4 w-4" />
                        Book a Call
                      </Button>
                    </a>
                  </div>
                </div>
              </div>

            </div>

            {/* Contact Form */}
            <Card className="border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-16 w-16 text-teal-600 mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-slate-600">
                      Thank you for reaching out. We'll get back to you within 24-48 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@example.com"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        data-testid="contact-phone"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="How can we help you?"
                        rows={5}
                        data-testid="contact-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-slate-900 hover:bg-slate-800" 
                      disabled={loading}
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="spinner"></span>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="h-4 w-4" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
}
